import { getAccessToken } from '../lib/supabaseClient';
// usePushNotifications.js
// Registers the service worker and handles push subscription.
// Call subscribe() after the user has saved their first log.

import { useState, useEffect } from 'react';

// Set this in your .env file:
//   Next.js:  NEXT_PUBLIC_VAPID_PUBLIC_KEY=your_key
//   CRA:      REACT_APP_VAPID_PUBLIC_KEY=your_key
// Generate keys with: npx web-push generate-vapid-keys
const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(char => char.charCodeAt(0)));
}

export function usePushNotifications(userEmail) {
  const [swReady, setSwReady] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);

  // Register service worker on mount
  useEffect(() => {
      if (typeof Notification !== 'undefined') {
        if (Notification.permission === 'granted' || Notification.permission === 'denied') {
          localStorage.setItem('apptnote_push_prompted', 'true');
        } else {
          // Permission is 'default' — clear the flag so prompt can show
          localStorage.removeItem('apptnote_push_prompted');
        }
      }
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker
      .register('/sw.js')
      .then(registration => {
        console.log('ApptNote SW registered:', registration.scope);
        setSwReady(true);
      })
      .catch(err => console.error('SW registration failed:', err));
  }, []);

  // Request permission and subscribe to push
  const subscribe = async () => {
    if (!swReady || !VAPID_PUBLIC_KEY) {
      console.warn('SW not ready or VAPID key missing');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') return false;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Save subscription to your backend
      const accessToken = await getAccessToken();
      if (!accessToken) return false;
      const res = await fetch('/api/save-push-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ subscription }),
      });
      if (!res.ok) return false;

      setSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      return false;
    }
  };

  const isSupported =
    'serviceWorker' in navigator && 'PushManager' in window;

  return {
    isSupported,
    swReady,
    permission,
    subscribed,
    subscribe,
  };
}
